<script setup lang="ts">
import { hsbToHls, sha } from "@/services/utils";
import { onMounted, ref, watch } from "vue";

const { name, email = "" } = defineProps<{
  name: string;
  email?: string;
}>();

const hasPfp = ref(false);
const pfpUrl = ref("");
const light = ref("");
const dark = ref("");
const text = ref("");

async function computeColors() {
  const hash = await sha(name);
  const h = Math.abs(parseInt(hash, 16) % 318) + 42;
  const d = hsbToHls(h, 71, 100);
  const l = hsbToHls((h - 42) % 360, 29, 98);
  const t = hsbToHls(h, 71, 50);

  light.value = `hsl(${l.h}deg ${l.s}% ${l.l}%)`;
  dark.value = `hsl(${d.h}deg ${d.s}% ${d.l}%)`;
  text.value = `hsl(${t.h}deg ${t.s}% ${t.l}%)`;
}

async function fetchGravatar() {
  if (email.length > 0) {
    const hash = await sha(email, "SHA-256");
    pfpUrl.value = `https://gravatar.com/avatar/${hash}`;
    const img = new Image();
    img.onload = () => {
      hasPfp.value = true;
    };
    img.src = pfpUrl.value;
  }
}

watch(
  () => name,
  async () => {
    await computeColors();
  }
);
onMounted(async () => {
  await computeColors();
  await fetchGravatar();
});
</script>

<template>
  <div
    class="avatar"
    :style="{
      '--light': light,
      '--dark': dark,
      '--text': text,
    }"
  >
    <div class="pfp">
      <img v-if="hasPfp" :src="pfpUrl" alt="profile picture" />
      <span v-else>
        {{ name[0] }}
      </span>
    </div>
    {{ name }}
  </div>
</template>

<style scoped>
.avatar {
  display: flex;
  overflow: hidden;
  flex-direction: row;
  align-items: center;
  color: var(--content-primary);
  gap: var(--numbers-4);
  text-overflow: ellipsis;
  white-space: nowrap;

  .pfp {
    display: flex;
    overflow: hidden;
    width: var(--numbers-24);
    height: var(--numbers-24);
    align-items: center;
    justify-content: center;
    border-radius: var(--numbers-12);
    background: linear-gradient(to top, var(--dark), var(--light));
    color: var(--text);
    font-size: var(--numbers-12);
  }
}
</style>
